import React from "react";
import {
  Drawer,
  Form,
  Select,
  Input,
  Divider,
  FormInstance,
  InputNumber,
  Row,
  Col,
} from "antd";
import { Product, RawMaterial, Recipe, RecipeIngredient } from "../../../Types";
import { ProductInfoCard } from "./ProductInfoCard";
import { IngredientPicker } from "./IngredientPicker";
import { IngredientsEditor } from "./IngredientsEditor";
import { RecipeCostSummary } from "./RecipeCostSummary";
import {
  DRAWER_DETAIL_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../../components";

const { Option } = Select;
const { TextArea } = Input;

interface Props {
  open: boolean;
  editingRecipe: Recipe | null;
  form: FormInstance;
  lookupProducts: Product[];
  recipes: Recipe[];
  allRawMaterials: RawMaterial[];
  currentSelectedProduct: any | null;
  ingredients: RecipeIngredient[];
  materialSearchText: string;
  ingredientsCost: number;
  additionalCost: number;
  batchSize: number;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onProductSearchChange: (v: string) => void;
  onProductSelect: (id: string) => void;
  onMaterialSearchChange: (v: string) => void;
  onAddIngredient: (id: string) => void;
  onUpdateIngredient: (
    index: number,
    updates: Partial<RecipeIngredient>,
  ) => void;
  onRemoveIngredient: (index: number) => void;
}

export const RecipeFormDrawer: React.FC<Props> = ({
  open,
  editingRecipe,
  form,
  lookupProducts,
  recipes,
  allRawMaterials,
  currentSelectedProduct,
  ingredients,
  materialSearchText,
  ingredientsCost,
  additionalCost,
  batchSize,
  isSubmitting,
  onClose,
  onSubmit,
  onProductSearchChange,
  onProductSelect,
  onMaterialSearchChange,
  onAddIngredient,
  onUpdateIngredient,
  onRemoveIngredient,
}) => (
  <Drawer
    title={drawerTitle(editingRecipe ? "Edit Recipe" : "Add Recipe")}
    width={DRAWER_DETAIL_WIDTH}
    onClose={onClose}
    open={open}
    styles={{ body: { paddingBottom: 80 } }}
    footer={drawerFooter({
      actions: [
        { label: "Cancel", onClick: onClose },
        {
          label: editingRecipe ? "Update Recipe" : "Make Recipe",
          type: "primary",
          loading: isSubmitting,
          onClick: onSubmit,
        },
      ],
    })}
  >
    <Form form={form} layout="vertical" className="flex flex-col gap-1">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="product_id"
            label="Product"
            rules={[{ required: true, message: "Select a product" }]}
          >
            <Select
              showSearch
              placeholder="Search and select product"
              disabled={!!editingRecipe}
              filterOption={false}
              onSearch={onProductSearchChange}
              onChange={onProductSelect}
            >
              {lookupProducts
                ?.filter((p) => {
                  if (editingRecipe)
                    return p.id == (editingRecipe.product_id as any);
                  return !recipes.some((r) => r.product_id == (p.id as any));
                })
                ?.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.title} ({p.sku_code || p.barcode})
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Col>

        {currentSelectedProduct?.stocks?.length > 0 && (
          <Col span={12}>
            <Form.Item
              name="product_stock_id"
              label="Product Stock Batch"
              rules={[{ required: true, message: "Select a stock batch" }]}
            >
              <Select placeholder="Select product stock batch">
                {currentSelectedProduct.stocks.map((st: any) => (
                  <Option key={st.id} value={st.id}>
                    {st.batch_no} • Qty: {st.quantity} • Rs.{" "}
                    {st.cost_price?.toFixed(2)}
                    {st.is_default === 1 ? " [Default]" : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>

      {currentSelectedProduct && (
        <ProductInfoCard product={currentSelectedProduct} />
      )}

      <div className="grid grid-cols-2 gap-4 mt-2">
        <Form.Item
          name="batch_size"
          label="Yield / Batch Size"
          rules={[{ required: true, message: "Batch size is required" }]}
        >
          <InputNumber
            min={0.001}
            className="w-full"
            placeholder="e.g. 100"
            precision={3}
          />
        </Form.Item>

        <Form.Item name="additional_cost" label="Additional Cost (Rs.)">
          <InputNumber
            min={0}
            className="w-full"
            placeholder="e.g. 50.00"
            precision={2}
          />
        </Form.Item>
      </div>

      <Divider className="my-1">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          Ingredients
        </span>
      </Divider>

      <div className="mb-3">
        <IngredientPicker
          allRawMaterials={allRawMaterials}
          searchText={materialSearchText}
          onSearchChange={onMaterialSearchChange}
          onAdd={onAddIngredient}
        />
      </div>

      <div className="mb-4">
        <IngredientsEditor
          ingredients={ingredients}
          allRawMaterials={allRawMaterials}
          onUpdate={onUpdateIngredient}
          onRemove={onRemoveIngredient}
        />
      </div>

      <RecipeCostSummary
        ingredientsCost={ingredientsCost}
        additionalCost={additionalCost}
        batchSize={batchSize}
        productCostPrice={currentSelectedProduct?.cost_price}
      />

      <Form.Item name="instructions" label="Instructions / Notes">
        <TextArea
          rows={4}
          placeholder="Cooking guidelines, prep steps, notes..."
        />
      </Form.Item>
    </Form>
  </Drawer>
);
